import React, { useState, useCallback, useEffect } from "react";
import axios from "axios";
import debounce from "lodash.debounce";
import Validator from "fastest-validator";
import {
  InputLabel,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  DialogContentText,
  Button,
  Input,
  InputAdornment,
  IconButton,
  FormHelperText,
  Grid,
  Autocomplete,
  TextField,
  Box,
  Tooltip,
  Typography,
} from "@mui/material";
import FormControl, { useFormControl } from "@mui/material/FormControl";
import { DialogContext, JoySnackBarContext } from "./context";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { styled } from "@mui/system";
import VerifiedIcon from "@mui/icons-material/Verified";
import LockIcon from "@mui/icons-material/Lock";
import EmailIcon from "@mui/icons-material/Email";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DoneOutlineIcon from "@mui/icons-material/DoneOutline";

import SmsIcon from "@mui/icons-material/Sms";
import { countryCodes } from "./data/countyCodes";

const config = require("../config/config");

const v = new Validator();

const usernameSchema = {
  username: {
    type: "string",
    min: 6,
    max: 255,
    pattern: /^[a-zA-Z0-9]+$/, // Alphanumeric without spaces
    messages: {
      stringMin: "Username must be at least 6 characters long",
      stringMax: "Username must not be longer than 255 characters",
      stringPattern: "Username must be alphanumeric without spaces",
    },
  },
};
const emailSchema = {
  email: {
    type: "email",
    messages: {
      email: "Email must be a valid email address",
    },
  },
};
const phoneSchemaIndia = {
  phone: {
    type: "string",
    length: 10,
    pattern: /^[0-9]+$/, // Numeric characters only
    messages: {
      length: "Phone number must be exactly 10 digits long.",
      stringPattern: "Phone number must only contain numeric digits",
    },
  },
};
const phoneSchemaWorld = {
  phone: {
    type: "string",
    pattern: /^[0-9]+$/, // Numeric characters only
    messages: {
      stringPattern: "Phone number must only contain numeric digits",
    },
  },
};

const firstNameSchema = {
  firstName: {
    type: "string",
    min: 3, // Minimum length of first name
    max: 50, // Maximum length of first name
    pattern: /^[a-zA-Z\s]*$/, // Alphabets and spaces only
    messages: {
      stringMin: "First Name must be at least 3 characters long",
      stringMax: "First Name must not be longer than 50 characters",
      stringPattern: "First Name must contain only letters and spaces",
    },
  },
};

const lastNameSchema = {
  lastName: {
    type: "string",
    min: 1, // Minimum length of last name
    max: 50, // Maximum length of last name
    pattern: /^[a-zA-Z\s]*$/, // Alphabets and spaces only
    messages: {
      stringMin: "Last Name must be at least 1 characters long",
      stringMax: "Last Name must not be longer than 50 characters",
      stringPattern: "Last Name must contain only letters and spaces",
    },
  },
};

const passwordSchema = {
  password: {
    type: "string",
    min: 8,
    max: 14,
    pattern:
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,14}$/,
    messages: {
      stringMin: "Password must be at least 8 characters long",
      stringMax: "Password must not be longer than 14 characters",
      stringPattern:
        "Password must contain at least one lowercase letter, one uppercase letter, one number, and one special character",
    },
  },
};

const otpSchema = {
  otp: {
    type: "string",
    length: 6,
    pattern: /^[0-9]{6}$/,
    messages: {
      stringPattern: "The OTP must be a 6-digit number.",
      stringLength: "The OTP must be exactly 6 digits long.",
    },
  },
};

// Create custom styles using styled
const DialogActionsMobile = styled(DialogActions)(({ theme }) => ({
  marginTop: theme.spacing(3),
  display: "flex",
  justifyContent: "center",
}));

const DialogActionsWeb = styled(DialogActions)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

function SignUpDialog({ signup }) {
  const { DialogDispatcher } = React.useContext(DialogContext);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [username, setUsername] = useState("");
  const [usernameErrors, setUsernameErrors] = useState([]);
  const [usernameAvailable, setUsernameAvailable] = useState(true);
  const [usernameLoading, setUsernameLoading] = useState(false);
  const [usernameDialogueAlertMessage, setUsernameDialogueAlertMessage] =
    useState("");
  const [email, setEmail] = useState("");
  const [emailErrors, setEmailErrors] = useState([]);
  const [emailAvailable, setEmailAvailable] = useState(true);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailDialogueAlertMessage, setEmailDialogueAlertMessage] =
    useState("");

  const [emailOTPDisable, setEmailOTPDisable] = useState(false);
  const [emailOTPRetryTimeLeft, setEmailOTPRetryTimeLeft] = useState(0);
  const [showEmailOTPEnterField, setShowEmailOTPEnterField] = useState(false);

  const [phoneOTPDisable, setPhoneOTPDisable] = useState(false);
  const [phoneOTPRetryTimeLeft, setPhoneOTPRetryTimeLeft] = useState(0);
  const [showPhoneOTPEnterField, setShowPhoneOTPEnterField] = useState(false);

  const [phone, setPhone] = useState("");
  const [phoneErrors, setPhoneErrors] = useState([]);
  const [phoneAvailable, setPhoneAvailable] = useState(true);
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [phoneDialogueAlertMessage, setPhoneDialogueAlertMessage] =
    useState("");

  const [firstName, setFirstName] = useState("");
  const [firstNameErrors, setFirstNameErrors] = useState([]);

  const [emailOTP, setEmailOTP] = useState("");
  const [emailOTPErrors, setEmailOTPErrors] = useState([]);
  const [isEmailVerified, setIsEmailVerified] = useState(false);

  const [phoneOTP, setPhoneOTP] = useState("");
  const [phoneOTPErrors, setPhoneOTPErrors] = useState([]);
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);

  const [lastName, setLastName] = useState("");
  const [lastNameErrors, setLastNameErrors] = useState([]);

  const [fullName, setFullName] = useState("");

  const [password, setPassword] = useState("");
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [showPassword, setShowPassword] = useState(true);

  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmPasswordErrors, setConfirmPasswordErrors] = useState([]);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [isPhoneCall, setIsPhoneCall] = useState(false);

  const [selectedCountry, setSelectedCountry] = useState({
    name: "India",
    label: "+91",
    emoji: "🇮🇳",
    code: "IN",
  });

  const { JoySnackBarDispatcher } = React.useContext(JoySnackBarContext);

  useEffect(() => {
    setFullName(`${firstName} ${lastName}`);

    console.log(`${firstName} ${lastName}`);
  }, [firstName, lastName]);

  const handleClose = () => {
    DialogDispatcher("OPEN", { signup: false });
  };

  const handleSignUp = async () => {
    try {
      const response = await axios.post(
        `${config.baseURL}/auth/temp-register`,
        {
          firstName: firstName,
          lastName: lastName,
          username: username,
          email: email,
          phone: phone,
          password: password,
          countryCode: selectedCountry.label,
        }
      );

      JoySnackBarDispatcher("OPEN", {
        vertical: "top",
        horizontal: "right",
        message: response.data.message,
        color: "success",
        size: "md",
        variant: "soft",
        iconName: "LoginSuccessful",
        iconColor: "success",
      });

      DialogDispatcher("OPEN", { signup: false });
    } catch (error) {
      console.error("Error at function : handleSignUp", error);

      JoySnackBarDispatcher("OPEN", {
        vertical: "top",
        horizontal: "right",
        message: error.response.data,
        color: "danger",
        size: "md",
        variant: "soft",
        iconName: "LogOutError",
        iconColor: "danger",
      });

      DialogDispatcher("OPEN", { signup: false });
    }
  };

  const isSignUpDisabled = () => {
    return (
      !isEmailVerified ||
      !isPhoneVerified ||
      !usernameAvailable ||
      usernameErrors.length > 0 ||
      lastNameErrors.length > 0 ||
      firstNameErrors.length > 0 ||
      !firstName ||
      !lastName ||
      !username ||
      passwordErrors.length > 0 ||
      !password ||
      confirmPasswordErrors.length > 0 ||
      !confirmPassword
    );
  };

  const handleGetMailOTPClick1 = async () => {
    try {
      const response = await axios.post(`${config.baseURL}/auth/email-otp`, {
        email: email,
        fullName: fullName,
      });

      if (response.status === 200) {
        JoySnackBarDispatcher("OPEN", {
          vertical: "top",
          horizontal: "right",
          message: response.data.snackAlertMessage,
          color: "success",
          size: "md",
          variant: "soft",
          iconName: "WrongPassword",
          iconColor: "success",
        });
        setEmailOTPDisable(true);

        setShowEmailOTPEnterField(true);

        setEmailOTPRetryTimeLeft(30); // Set the timer to 30 seconds

        const intervalId = setInterval(() => {
          setEmailOTPRetryTimeLeft((prevTime) => {
            if (prevTime <= 1) {
              clearInterval(intervalId);
              setEmailOTPDisable(false);
              return 0;
            }
            return prevTime - 1;
          });
        }, 1000);

        setTimeout(() => {
          setEmailOTPDisable(false);
          clearInterval(intervalId); // Ensure interval is cleared
        }, 30000);
      }
    } catch (error) {
      console.error("Error at function : handleGetMailOtpClick", error);
    } finally {
    }
  };

  const handleOtherCountryPhoneVerify = () => {
    JoySnackBarDispatcher("OPEN", {
      vertical: "top",
      horizontal: "right",
      message: "Phone selected successfully",
      color: "success",
      size: "md",
      variant: "soft",
      iconName: "WrongPassword",
      iconColor: "success",
    });

    setIsPhoneVerified(true);
  };

  const handleGetPhoneOTPClick = async () => {
    try {
      const response = await axios.post(`${config.baseURL}/auth/phone-otp`, {
        phone: phone,
        fullName: fullName,
        isCall: isPhoneCall,
      });

      if (response.status === 200) {
        JoySnackBarDispatcher("OPEN", {
          vertical: "top",
          horizontal: "right",
          message: response.data.snackAlertMessage,
          color: "success",
          size: "md",
          variant: "soft",
          iconName: "WrongPassword",
          iconColor: "success",
        });
        setPhoneOTPDisable(true);

        setShowPhoneOTPEnterField(true);

        setPhoneOTPRetryTimeLeft(30); // Set the timer to 30 seconds

        const intervalId = setInterval(() => {
          setPhoneOTPRetryTimeLeft((prevTime) => {
            if (prevTime <= 1) {
              clearInterval(intervalId);
              setPhoneOTPDisable(false);
              return 0;
            }
            return prevTime - 1;
          });
        }, 1000);

        setTimeout(() => {
          setPhoneOTPDisable(false);
          clearInterval(intervalId); // Ensure interval is cleared
        }, 30000);
      }
    } catch (error) {
      console.error("Error at function : handleGetPhoneOTPClick", error);
    } finally {
    }
  };

  const handleVerifyMailOTPClick = async () => {
    try {
      const response = await axios.post(
        `${config.baseURL}/auth/email-otp-verify`,
        {
          email: email,
          otp: emailOTP,
        }
      );

      if (response.status === 200) {
        JoySnackBarDispatcher("OPEN", {
          vertical: "top",
          horizontal: "right",
          message: response.data.snackAlertMessage,
          color: "success",
          size: "md",
          variant: "soft",
          iconName: "WrongPassword",
          iconColor: "success",
        });
        // setEmailOTPDisable(true);

        // setShowEmailOTPEnterField(true);

        // setEmailOTPRetryTimeLeft(30); // Set the timer to 30 seconds

        // const intervalId = setInterval(() => {
        //   setEmailOTPRetryTimeLeft((prevTime) => {
        //     if (prevTime <= 1) {
        //       clearInterval(intervalId);
        //       setEmailOTPDisable(false);
        //       return 0;
        //     }
        //     return prevTime - 1;
        //   });
        // }, 1000);

        // setTimeout(() => {
        //   setEmailOTPDisable(false);
        //   clearInterval(intervalId); // Ensure interval is cleared
        // }, 30000);

        setShowEmailOTPEnterField(false);
        setIsEmailVerified(true);
        setEmailDialogueAlertMessage(`"${email}" : email is verified`);
      }
    } catch (error) {
      console.error("Error at function : handleVerifyMailOTPClick", error);

      JoySnackBarDispatcher("OPEN", {
        vertical: "top",
        horizontal: "right",
        message: error.response.data.snackAlertMessage,
        color: "danger",
        size: "md",
        variant: "soft",
        iconName: "WrongPassword",
        iconColor: "danger",
      });
    } finally {
    }
  };

  const handleVerifyPhoneOTPClick = async () => {
    try {
      const response = await axios.post(
        `${config.baseURL}/auth/phone-otp-verify`,
        {
          phone: phone,
          otp: phoneOTP,
        }
      );

      if (response.status === 200) {
        JoySnackBarDispatcher("OPEN", {
          vertical: "top",
          horizontal: "right",
          message: response.data.snackAlertMessage,
          color: "success",
          size: "md",
          variant: "soft",
          iconName: "WrongPassword",
          iconColor: "success",
        });
        // setEmailOTPDisable(true);

        // setShowEmailOTPEnterField(true);

        // setEmailOTPRetryTimeLeft(30); // Set the timer to 30 seconds

        // const intervalId = setInterval(() => {
        //   setEmailOTPRetryTimeLeft((prevTime) => {
        //     if (prevTime <= 1) {
        //       clearInterval(intervalId);
        //       setEmailOTPDisable(false);
        //       return 0;
        //     }
        //     return prevTime - 1;
        //   });
        // }, 1000);

        // setTimeout(() => {
        //   setEmailOTPDisable(false);
        //   clearInterval(intervalId); // Ensure interval is cleared
        // }, 30000);

        setShowPhoneOTPEnterField(false);
        setIsPhoneVerified(true);
        setPhoneDialogueAlertMessage(`"${phone}" : phone number is verified`);
      }
    } catch (error) {
      console.error("Error at function : handleVerifyPhoneOTPClick", error);

      JoySnackBarDispatcher("OPEN", {
        vertical: "top",
        horizontal: "right",
        message: error.response.data.snackAlertMessage,
        color: "danger",
        size: "md",
        variant: "soft",
        iconName: "WrongPassword",
        iconColor: "danger",
      });
    } finally {
    }
  };

  const UsernameHelperText = () => {
    const { focused } = useFormControl() || {};

    const helperText = React.useMemo(() => {
      if (focused) {
        return usernameErrors.join(", ");
      }

      return "";
    }, [focused]);

    return <FormHelperText>{helperText}</FormHelperText>;
  };

  const handleUsernameChange = async (newUsername) => {
    try {
      setUsernameLoading(true);
      const response = await axios.post(
        `${config.baseURL}/auth/username-verify`,
        {
          username: newUsername,
        }
      );

      setUsernameDialogueAlertMessage(
        `"${newUsername}" : ${response.data.dialogueAlertMessage}`
      );
      setUsernameAvailable(true);
    } catch (error) {
      console.error("Error during username verification:", error);
      if (error.response && error.response.data) {
        if (error.response.status === 409) {
          setUsernameAvailable(false);
          setUsernameDialogueAlertMessage(
            `"${newUsername}" : ${error.response.data.dialogueAlertMessage}`
          );
        }
      } else {
        setUsernameAvailable(false);
        setUsernameDialogueAlertMessage(
          "Something went wrong. Please try again later."
        );
      }
    } finally {
      setUsernameLoading(false);
    }
  };

  const debouncedHandleUsernameChange = useCallback(
    debounce(handleUsernameChange, 400),
    []
  );

  const handleUsernameInputChange = (e) => {
    const newUsername = e.target.value;
    setUsername(newUsername);

    // Validate username using fastest-validator
    const validationResponse = v.validate(
      { username: newUsername },
      usernameSchema
    );

    if (validationResponse !== true) {
      setUsernameErrors(validationResponse.map((err) => err.message));
      setUsernameAvailable(false);
      setUsernameDialogueAlertMessage(`"${newUsername}" : username is INVALID`);
      return;
    } else {
      setUsernameErrors([]);
      debouncedHandleUsernameChange(newUsername);
    }
  };

  const EmailHelperText = () => {
    const { focused } = useFormControl() || {};

    const helperText = React.useMemo(() => {
      if (focused) {
        return emailErrors.join(", ");
      }

      return "";
    }, [focused]);

    return <FormHelperText>{helperText}</FormHelperText>;
  };

  const handleEmailChange = async (newEmail) => {
    try {
      setEmailLoading(true);
      const response = await axios.post(`${config.baseURL}/auth/email-verify`, {
        email: newEmail,
      });

      setEmailDialogueAlertMessage(
        `"${newEmail}" : ${response.data.dialogueAlertMessage}`
      );
      setEmailAvailable(true);
    } catch (error) {
      console.error("Error during email verification:", error);
      if (error.response && error.response.data) {
        if (error.response.status === 409) {
          setEmailAvailable(false);
          setEmailDialogueAlertMessage(
            `"${newEmail}" : ${error.response.data.dialogueAlertMessage}`
          );
        }
      } else {
        setEmailAvailable(false);
        setEmailDialogueAlertMessage(
          "Something went wrong. Please try again later."
        );
      }
    } finally {
      setEmailLoading(false);
    }
  };

  const debouncedHandleEmailChange = useCallback(
    debounce(handleEmailChange, 400),
    []
  );

  const handleEmailInputChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);

    const validationResponse = v.validate({ email: newEmail }, emailSchema);

    if (validationResponse !== true) {
      setEmailErrors(validationResponse.map((err) => err.message));
      setEmailAvailable(false);
      setEmailDialogueAlertMessage(`"${newEmail}" : email is INVALID`);
      return;
    } else {
      setEmailErrors([]);
      debouncedHandleEmailChange(newEmail);
    }
  };

  const MyPhoneHelperText = () => {
    const { focused } = useFormControl() || {};

    const helperText = React.useMemo(() => {
      if (focused) {
        return phoneErrors.join(", ");
      }

      return "";
    }, [focused]);

    return <FormHelperText>{helperText}</FormHelperText>;
  };

  const handlePhoneChange = async (newPhone) => {
    try {
      setPhoneLoading(true);
      const response = await axios.post(`${config.baseURL}/auth/phone-verify`, {
        phone: newPhone,
      });

      setPhoneDialogueAlertMessage(
        `"${newPhone}" : ${response.data.dialogueAlertMessage}`
      );
      setPhoneAvailable(true);
    } catch (error) {
      console.error("Error during phone verification:", error);
      if (error.response && error.response.data) {
        if (error.response.status === 409) {
          setPhoneAvailable(false);
          setPhoneDialogueAlertMessage(
            `"${newPhone}" : ${error.response.data.dialogueAlertMessage}`
          );
        }
      } else {
        setPhoneAvailable(false);
        setPhoneDialogueAlertMessage(
          "Something went wrong. Please try again later."
        );
      }
    } finally {
      setPhoneLoading(false);
    }
  };

  const debouncedHandlePhoneChange = useCallback(
    debounce(handlePhoneChange, 400),
    []
  );

  const handlePhoneResponsiveness = (fastestGettingCountryCode) => {
    setShowPhoneOTPEnterField(false);
    const validationResponse = v.validate(
      { phone: phone },
      fastestGettingCountryCode.code === "IN"
        ? phoneSchemaIndia
        : phoneSchemaWorld
    );

    if (validationResponse !== true) {
      setPhoneErrors(validationResponse.map((err) => err.message));
      setPhoneAvailable(false);
      setPhoneDialogueAlertMessage(`"${phone}" : phone number is INVALID`);
      return;
    } else {
      setPhoneErrors([]);
      debouncedHandlePhoneChange(phone);
    }
  };

  const handlePhoneInputChange = (e) => {
    const newPhone = e.target.value;
    setPhone(newPhone);

    const validationResponse = v.validate(
      { phone: newPhone },
      selectedCountry.code === "IN" ? phoneSchemaIndia : phoneSchemaWorld
    );

    if (validationResponse !== true) {
      setPhoneErrors(validationResponse.map((err) => err.message));
      setPhoneAvailable(false);
      setPhoneDialogueAlertMessage(`"${newPhone}" : phone number is INVALID`);
      return;
    } else {
      setPhoneErrors([]);
      debouncedHandlePhoneChange(newPhone);
    }
  };

  const MyFirstNameHelperText = () => {
    const { focused } = useFormControl() || {};

    const helperText = React.useMemo(() => {
      if (focused) {
        return firstNameErrors.join(", ");
      }

      return "";
    }, [focused]);

    return <FormHelperText>{helperText}</FormHelperText>;
  };

  const handleFirstNameInputChange = (e) => {
    const newFirstName = e.target.value;
    setFirstName(newFirstName);

    // Validate firstName using fastest-validator
    const validationResponse = v.validate(
      { firstName: newFirstName },
      firstNameSchema
    );

    if (validationResponse !== true) {
      setFirstNameErrors(validationResponse.map((err) => err.message));
      return;
    } else {
      setFirstNameErrors([]);
    }
  };

  const MyEmailOTPHelperText = () => {
    const { focused } = useFormControl() || {};

    const helperText = React.useMemo(() => {
      if (focused) {
        return emailOTPErrors.join(", ");
      }

      return "";
    }, [focused]);

    return <FormHelperText>{helperText}</FormHelperText>;
  };

  const handleEmailOTPInputChange = (e) => {
    const newEmailOTP = e.target.value;
    setEmailOTP(newEmailOTP);

    // Validate EmailOTP using fastest-validator
    const validationResponse = v.validate({ otp: newEmailOTP }, otpSchema);

    if (validationResponse !== true) {
      setEmailOTPErrors(validationResponse.map((err) => err.message));
      return;
    } else {
      setEmailOTPErrors([]);
    }
  };

  const MyPhoneOTPHelperText = () => {
    const { focused } = useFormControl() || {};

    const helperText = React.useMemo(() => {
      if (focused) {
        return phoneOTPErrors.join(", ");
      }

      return "";
    }, [focused]);

    return <FormHelperText>{helperText}</FormHelperText>;
  };

  const handlePhoneOTPInputChange = (e) => {
    const newPhoneOTP = e.target.value;
    setPhoneOTP(newPhoneOTP);

    // Validate PhoneOTP using fastest-validator
    const validationResponse = v.validate({ otp: newPhoneOTP }, otpSchema);

    if (validationResponse !== true) {
      setPhoneOTPErrors(validationResponse.map((err) => err.message));
      return;
    } else {
      setPhoneOTPErrors([]);
    }
  };

  const MyLastNameHelperText = () => {
    const { focused } = useFormControl() || {};

    const helperText = React.useMemo(() => {
      if (focused) {
        return lastNameErrors.join(", ");
      }

      return "";
    }, [focused]);

    return <FormHelperText>{helperText}</FormHelperText>;
  };

  const handleLastNameInputChange = (e) => {
    const newLastName = e.target.value;
    setLastName(newLastName);

    // Validate last name using fastest-validator
    const validationResponse = v.validate(
      { lastName: newLastName },
      lastNameSchema
    );

    if (validationResponse !== true) {
      setLastNameErrors(validationResponse.map((err) => err.message));

      return;
    } else {
      setLastNameErrors([]);
      // Perform any necessary actions upon valid last name input
    }
  };

  const handlePasswordInputChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);

    const validationResponse = v.validate(
      { password: newPassword },
      passwordSchema
    );

    if (validationResponse !== true) {
      setPasswordErrors(validationResponse.map((err) => err.message));
    } else {
      setPasswordErrors([]);
    }
  };

  const handleConfirmPasswordInputChange = (e) => {
    const newConfirmPassword = e.target.value;
    setConfirmPassword(newConfirmPassword);

    if (newConfirmPassword !== password) {
      setConfirmPasswordErrors(["Passwords do not match"]);
    } else {
      setConfirmPasswordErrors([]);
    }
  };

  const PasswordHelperText = () => {
    const { focused } = useFormControl() || {};

    const helperText = React.useMemo(() => {
      if (focused) {
        return passwordErrors.join(", ");
      }

      return "";
    }, [focused]);

    return <FormHelperText>{helperText}</FormHelperText>;
  };

  const ConfirmPasswordHelperText = () => {
    const { focused } = useFormControl() || {};

    const helperText = React.useMemo(() => {
      if (focused) {
        return confirmPasswordErrors.join(", ");
      }

      return "";
    }, [focused]);

    return <FormHelperText>{helperText}</FormHelperText>;
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleClickShowConfirmPassword = () =>
    setShowConfirmPassword((show) => !show);

  const handleMouseDownConfirmPassword = (event) => {
    event.preventDefault();
  };

  return (
    <div>
      <Dialog open={signup} onClose={handleClose} fullScreen={fullScreen}>
        <DialogTitle>Sign Up</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please fill out the form below to create an account.
          </DialogContentText>

          <Box
            display="flex"
            flexDirection={isSmallScreen ? "column" : "row"}
            justifyContent="space-between"
            sx={{ gap: isSmallScreen ? 0 : 2 }}
          >
            <FormControl
              required
              error={firstNameErrors.length > 0}
              color={firstNameErrors.length > 0 ? "error" : "success"}
              fullWidth
              sx={{ mt: 1, mb: 0.5 }}
              variant="standard"
            >
              <InputLabel htmlFor="standard-adornment-firstName">
                First Name
              </InputLabel>
              <Input
                id="standard-adornment-firstName"
                color={firstNameErrors.length > 0 ? "error" : "success"}
                type="text"
                value={firstName}
                onChange={handleFirstNameInputChange}
              />
              <MyFirstNameHelperText />
            </FormControl>

            <FormControl
              required
              error={lastNameErrors.length > 0}
              color={lastNameErrors.length > 0 ? "error" : "success"}
              fullWidth
              sx={{ mt: 1, mb: 0.5 }}
              variant="standard"
            >
              <InputLabel htmlFor="standard-adornment-lastname">
                Last Name
              </InputLabel>
              <Input
                id="standard-adornment-lastname"
                color={lastNameErrors.length > 0 ? "error" : "success"}
                type="text"
                value={lastName}
                onChange={handleLastNameInputChange}
              />
              <MyLastNameHelperText />
            </FormControl>
          </Box>

          <FormControl
            required
            error={!usernameAvailable || usernameErrors.length > 0}
            color={
              !usernameAvailable || usernameErrors.length > 0
                ? "error"
                : "success"
            }
            fullWidth
            sx={{ mt: 1, mb: 0.5 }}
            variant="standard"
          >
            <InputLabel htmlFor="standard-adornment-username">
              Username
            </InputLabel>
            <Input
              id="standard-adornment-username"
              color={
                !usernameAvailable || usernameErrors.length > 0
                  ? "error"
                  : "success"
              }
              type="text"
              value={username}
              onChange={handleUsernameInputChange}
            />
            <FormHelperText
              id="standard-weight-helper-text"
              sx={{
                color:
                  !usernameAvailable || usernameErrors.length > 0
                    ? "error.main"
                    : "success.main",
              }}
            >
              {usernameDialogueAlertMessage}
            </FormHelperText>
            <UsernameHelperText />
          </FormControl>

          <FormControl
            required
            error={!emailAvailable || emailErrors.length > 0}
            color={
              !emailAvailable || emailErrors.length > 0 ? "error" : "success"
            }
            fullWidth
            sx={{ mt: 1, mb: 0.5 }}
            variant="standard"
          >
            <InputLabel htmlFor="standard-adornment-email">Email</InputLabel>
            <Input
              id="standard-adornment-email"
              color={
                !emailAvailable || emailErrors.length > 0 ? "error" : "success"
              }
              type="email"
              disabled={isEmailVerified}
              value={email}
              onChange={handleEmailInputChange}
              endAdornment={
                isEmailVerified ? (
                  <VerifiedIcon sx={{ color: "blue" }} />
                ) : (
                  <InputAdornment position="end">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleGetMailOTPClick1}
                      sx={{ mb: 1, mr: 0.5 }}
                      startIcon={
                        emailOTPDisable ? (
                          <CheckCircleOutlineIcon />
                        ) : (
                          <EmailIcon />
                        )
                      }
                      disabled={
                        !emailAvailable ||
                        emailErrors.length > 0 ||
                        email === "" ||
                        emailOTPDisable
                      }
                    >
                      {emailOTPDisable
                        ? `${emailOTPRetryTimeLeft} Sec`
                        : "Get OTP"}
                    </Button>
                  </InputAdornment>
                )
              }
            />
            <FormHelperText
              id="standard-weight-helper-text-email"
              sx={{
                color:
                  !emailAvailable || emailErrors.length > 0
                    ? "error.main"
                    : "success.main",
              }}
            >
              {emailDialogueAlertMessage}
            </FormHelperText>
            <EmailHelperText />
          </FormControl>

          <Box
            display="flex"
            flexDirection={isSmallScreen ? "column" : "row"}
            justifyContent="space-between"
            sx={{ gap: isSmallScreen ? 0 : 2 }}
          >
            {showEmailOTPEnterField && (
              <FormControl
                required
                error={emailOTPErrors.length > 0}
                color={emailOTPErrors.length > 0 ? "error" : "success"}
                fullWidth
                sx={{ mt: 1, mb: 0.5 }}
                variant="standard"
              >
                <InputLabel htmlFor="standard-adornment-firstName">
                  Email OTP
                </InputLabel>
                <Input
                  id="standard-adornment-firstName"
                  color={emailOTPErrors.length > 0 ? "error" : "success"}
                  type="text"
                  value={emailOTP}
                  onChange={handleEmailOTPInputChange}
                  endAdornment={
                    <InputAdornment position="end">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleVerifyMailOTPClick}
                        sx={{ mb: 1, mr: 0.5 }}
                        startIcon={<DoneOutlineIcon />}
                        disabled={
                          emailOTPErrors.length > 0 || emailOTP === ""
                          //|| emailOTPDisable
                        }
                      >
                        Verify OTP
                      </Button>
                    </InputAdornment>
                  }
                />
                <MyEmailOTPHelperText />
              </FormControl>
            )}
            {showPhoneOTPEnterField && (
              <FormControl
                required
                error={phoneOTPErrors.length > 0}
                color={phoneOTPErrors.length > 0 ? "error" : "success"}
                fullWidth
                sx={{ mt: 1, mb: 0.5 }}
                variant="standard"
              >
                <InputLabel htmlFor="standard-adornment-lastname">
                  Phone OTP
                </InputLabel>
                <Input
                  id="standard-adornment-lastname"
                  color={phoneOTPErrors.length > 0 ? "error" : "success"}
                  type="text"
                  value={phoneOTP}
                  onChange={handlePhoneOTPInputChange}
                  endAdornment={
                    <InputAdornment position="end">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleVerifyPhoneOTPClick}
                        sx={{ mb: 1, mr: 0.5 }}
                        startIcon={<DoneOutlineIcon />}
                        disabled={
                          phoneOTPErrors.length > 0 || phoneOTP === ""
                          //|| emailOTPDisable
                        }
                      >
                        Verify OTP
                      </Button>
                    </InputAdornment>
                  }
                />
                <MyPhoneOTPHelperText />
              </FormControl>
            )}
          </Box>

          <Grid
            container
            //  spacing={1}
            alignItems="center"
            sx={{ mt: 1, mb: 0.5 }}
          >
            <Grid item xs={2.1}>
              <Autocomplete
                defaultValue={selectedCountry}
                id="country-selector"
                options={countryCodes}
                autoHighlight
                disableClearable
                getOptionLabel={(option) => `${option.label}`}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Code"
                    variant="standard"
                    inputProps={{
                      ...params.inputProps,
                    }}
                  />
                )}
                onChange={(event, newValue) => {
                  setSelectedCountry(newValue);
                  handlePhoneResponsiveness(newValue);
                }}
              />
            </Grid>
            <Grid item xs={1.7}>
              <Typography align="center" variant="h3" sx={{ p: 0 }}>
                {" "}
                {selectedCountry ? selectedCountry.emoji : ""}{" "}
              </Typography>
            </Grid>
            <Grid item xs={8.2}>
              <FormControl
                required
                error={!phoneAvailable || phoneErrors.length > 0}
                color={
                  !phoneAvailable || phoneErrors.length > 0
                    ? "error"
                    : "success"
                }
                fullWidth
                sx={{ mt: 1, mb: 0.5 }}
                variant="standard"
              >
                <InputLabel htmlFor="standard-adornment-phone">
                  Phone
                </InputLabel>
                <Input
                  id="standard-adornment-phone"
                  color={
                    !phoneAvailable || phoneErrors.length > 0
                      ? "error"
                      : "success"
                  }
                  type="text"
                  disabled={isPhoneVerified}
                  value={phone}
                  onChange={handlePhoneInputChange}
                  endAdornment={
                    isPhoneVerified ? (
                      <VerifiedIcon sx={{ color: "blue" }} />
                    ) : selectedCountry.code === "IN" ? (
                      <InputAdornment position="end">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={handleGetPhoneOTPClick}
                          sx={{ mb: 1, mr: 0.5 }}
                          startIcon={
                            phoneOTPDisable ? (
                              <CheckCircleOutlineIcon />
                            ) : (
                              <SmsIcon />
                            )
                          }
                          disabled={
                            !phoneAvailable ||
                            phoneErrors.length > 0 ||
                            phone === "" ||
                            phoneOTPDisable
                          }
                        >
                          {phoneOTPDisable
                            ? `${phoneOTPRetryTimeLeft} Sec`
                            : "Get OTP"}
                        </Button>
                      </InputAdornment>
                    ) : (
                      <InputAdornment position="end">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={handleOtherCountryPhoneVerify}
                          sx={{ mb: 1, mr: 0.5 }}
                          startIcon={<CheckCircleOutlineIcon />}
                          disabled={
                            !phoneAvailable ||
                            phoneErrors.length > 0 ||
                            phone === ""
                          }
                        >
                          Select
                        </Button>
                      </InputAdornment>
                    )
                  }
                />
                <FormHelperText
                  id="standard-weight-helper-text-phone"
                  sx={{
                    color:
                      !phoneAvailable || phoneErrors.length > 0
                        ? "error.main"
                        : "success.main",
                  }}
                >
                  {phoneDialogueAlertMessage}
                </FormHelperText>
                <MyPhoneHelperText />
              </FormControl>
            </Grid>
          </Grid>

          <Box
            display="flex"
            flexDirection={isSmallScreen ? "column" : "row"}
            justifyContent="space-between"
            sx={{ gap: isSmallScreen ? 0 : 2 }}
          >
            <FormControl
              required
              error={passwordErrors.length > 0}
              color={passwordErrors.length > 0 ? "error" : "success"}
              fullWidth
              sx={{ mt: 1, mb: 0.5 }}
              variant="standard"
            >
              <InputLabel htmlFor="standard-adornment-password">
                Password
              </InputLabel>
              <Input
                id="standard-adornment-password"
                color={passwordErrors.length > 0 ? "error" : "success"}
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={handlePasswordInputChange}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                }
              />
              <PasswordHelperText />
            </FormControl>

            <FormControl
              required
              error={confirmPasswordErrors.length > 0}
              color={confirmPasswordErrors.length > 0 ? "error" : "success"}
              fullWidth
              sx={{ mt: 1, mb: 0.5 }}
              variant="standard"
            >
              <InputLabel htmlFor="standard-adornment-confirm-password">
                Confirm Password
              </InputLabel>
              <Input
                id="standard-adornment-confirm-password"
                color={confirmPasswordErrors.length > 0 ? "error" : "success"}
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={handleConfirmPasswordInputChange}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowConfirmPassword}
                      onMouseDown={handleMouseDownConfirmPassword}
                    >
                      {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                }
                // onKeyDown={(e) => {
                //   if (e.key === "Enter") {
                //     handleSignIn(identifier, password);
                //   }
                // }}
              />
              <ConfirmPasswordHelperText />
            </FormControl>
          </Box>
          {fullScreen ? (
            <DialogActionsMobile>
              <Button
                variant="outlined"
                color="error"
                onClick={handleClose}
                sx={{ mr: (theme) => theme.spacing(5) }}
              >
                Cancel
              </Button>

              <Tooltip
                title={
                  isSignUpDisabled()
                    ? "Fill all required details to enable sign up button"
                    : ""
                }
              >
                <span>
                  <Button
                    variant="contained"
                    color="success"
                    disabled={isSignUpDisabled()}
                    onClick={handleSignUp}
                  >
                    Sign Up
                  </Button>
                </span>
              </Tooltip>
            </DialogActionsMobile>
          ) : (
            <DialogActionsWeb>
              <Button
                variant="outlined"
                color="error"
                onClick={handleClose}
                sx={{ mr: (theme) => theme.spacing(1) }}
              >
                Cancel
              </Button>

              <Tooltip
                title={
                  isSignUpDisabled()
                    ? "Fill all required details to enable sign up button"
                    : ""
                }
              >
                <span>
                  <Button
                    variant="contained"
                    color="success"
                    disabled={isSignUpDisabled()}
                    onClick={handleSignUp}
                  >
                    Sign Up
                  </Button>
                </span>
              </Tooltip>
            </DialogActionsWeb>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default SignUpDialog;
